'use client';

import type React from 'react';
import { useState, useRef, useEffect } from 'react';
import styles from './tooltip.module.scss';

interface TooltipProps {
  trigger: React.ReactNode;
  content: React.ReactNode;
}

/** Clickable Tooltip */
const Tooltip = ({ trigger, content }: TooltipProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className={styles.tooltipContainer}>
      <button
        ref={triggerRef}
        onClick={handleClick}
        className={styles.tooltipTrigger}
      >
        {trigger}
      </button>

      {isOpen && (
        <div
          ref={tooltipRef}
          className={styles.tooltipContent}
        >
          <button
            onClick={handleClose}
            className={styles.closeButton}
            aria-label="Close tooltip"
          >
            <span className={styles.closeIcon}></span>
          </button>
          <div className={styles.tooltipBody}>{content}</div>
        </div>
      )}
    </div>
  );
};

export default Tooltip;
